import { TeachingLoadReportType, TimetableReportType } from '@bp/planung-graphql-types';
import dayjs from 'dayjs';
import { TimetableReportsTableType } from '../components/EvaluateTimetable/TimetableReportsTable';
import { ReportType } from '../components/EvaluateTimetable/types';
import { apiHelper } from './apiHelper';
import { showSuccessCreateToast, showSuccessDeleteToast, showUserDbErrorToast } from './toast';

export const downloadReport = async (
  report: TimetableReportsTableType,
  reportType: TeachingLoadReportType | TimetableReportType,
) => {
  const downloadFileName = report.name.replace(/ /g, '_') + '_' + report.timestamp.format('YYYY-MM-DD');

  return await apiHelper<void>({
    method: 'GET',
    path: `/pdf/${reportType}/${report.uuid}`,
    type: 'download',
    downloadFileName,
  });
};

/** Report-Typen, die der `/pdf/display`-Endpunkt direkt (ohne Job/Report-Datensatz) rendern kann. */
export type DisplayReportType =
  | TimetableReportType.SingleClass
  | TimetableReportType.SingleRoom
  | TimetableReportType.SingleTeacher;

/**
 * Erzeugt die Pläne einer Stundenplan-Version direkt und lädt sie sofort herunter – es entsteht
 * kein Report-Datensatz, der Download taucht also in keiner Liste auf (identisch zum "Drucken"
 * an der Pinnwand).
 */
export const downloadDisplayReport = async ({
  reportType,
  timetableVersionUuid,
  contextUuids,
  downloadFileName,
}: {
  reportType: DisplayReportType;
  timetableVersionUuid: string;
  contextUuids: string[];
  downloadFileName: string;
}) => {
  return await apiHelper<void>({
    method: 'GET',
    path: `/pdf/display/${reportType}/${timetableVersionUuid}/${contextUuids.join(',')}`,
    type: 'download',
    downloadFileName,
  });
};

export const deleteReport = async (
  report: { uuid: string; name: string },
  reportType: TeachingLoadReportType | TimetableReportType,
) => {
  const response = await apiHelper<{ message: string }>({
    method: 'DELETE',
    path: `/pdf/${reportType}/${report.uuid}`,
  });

  if (response) {
    showSuccessDeleteToast();
  } else {
    showUserDbErrorToast();
  }

  return response;
};

export const downloadDebug = async (uuid: string, type: ReportType) => {
  return await apiHelper<void>({
    method: 'GET',
    path: `/pdf/export/${type}/${uuid}`,
    type: 'download',
    downloadFileName: type + '_' + uuid + '_' + dayjs().format('YYYY-MM-DD') + '.json',
  });
};

export const createTimetableCollectiveReport = async (
  uuid: string,
  type: ReportType,
  subType: TimetableReportType | undefined,
) => {
  const response = await apiHelper<{ uuid: string }>({
    method: 'POST',
    path: `/pdf/${type}/${subType}/${uuid}`,
    type: 'json',
  });

  if (response) {
    showSuccessCreateToast();
  } else {
    showUserDbErrorToast();
  }

  return response;
};

export const createTimetableSingleReport = async (
  uuid: string,
  type: ReportType,
  subType: TimetableReportType | undefined,
) => {
  const response = await apiHelper<{ uuid: string }>({
    method: 'POST',
    path: `/pdf/timetablezip/${subType}/${uuid}`,
  });

  if (response) {
    showSuccessCreateToast();
  } else {
    showUserDbErrorToast();
  }

  return response;
};

export const createTeachingLoadReport = async (
  uuid: string,
  scenario: string,
  type: ReportType,
  subType: TimetableReportType | TeachingLoadReportType | undefined,
) => {
  const response = await apiHelper<Response>({
    method: 'POST',
    path: `/pdf/${type}/${subType}/${uuid}/${scenario}`,
  });

  if (response) {
    showSuccessCreateToast();
  } else {
    showUserDbErrorToast();
  }

  return response;
};
